import {
  SplitSide,
  useSceneEvents,
} from "@/components/common/scene-events-context";
import { useStoreActiveCameras } from "@/components/common/view-runtime-context";
import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import {
  showEverything,
  SnapshotRenderer,
} from "@/components/r3f/renderers/snapshot-renderer";
import { useCameraParametersIfAvailable } from "@/components/r3f/utils/camera-parameters";
import { CameraSync } from "@/components/r3f/utils/camera-sync";
import { MiniMapActions } from "@/components/r3f/utils/minimap/minimap";
import { useOverlayElements } from "@/modes/overlay-elements-context";
import {
  selectBestModelCameraFor360,
  selectBestWayPointForElement,
} from "@/modes/walk-mode/animations/pano-to-model";
import { WalkScene } from "@/modes/walk-mode/walk-scene";
import { ViewType, WalkSceneActiveElement } from "@/modes/walk-mode/walk-types";
import {
  FeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  selectPhotoObject,
  selectShouldSyncCamerasOnWaypoint,
  selectShouldSyncCamerasRotation,
  setCompareElementId,
  setSyncCamerasOnWaypoint,
  setSyncCamerasRotation,
} from "@/store/modes/walk-mode-slice";
import { curryAppSelector } from "@/store/reselect";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { getCameraAnimationTime } from "@/utils/camera-animation-time";
import { selectPanoCameraTransform } from "@/utils/camera-transform";
import {
  IElementsState,
  selectHasValidPose,
  selectIElementWorldPosition,
  useNonExhaustiveEffect,
  useTypedEvent,
  View,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  IElementImg360,
  isIElementGenericPointCloudStream,
  isIElementImageCommand,
  isIElementImg2d,
  isIElementImg360,
  isIElementModel3dStream,
  type IElementImageCommand,
} from "@faro-lotv/ielement-types";
import {
  assert,
  RAD_TO_DEG,
  reproportionCamera,
  SupportedCamera,
} from "@faro-lotv/lotv";
import { useThree, Vector3 as Vector3Prop } from "@react-three/fiber";
import { createSelector } from "@reduxjs/toolkit";
import {
  Suspense,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Camera, OrthographicCamera, PerspectiveCamera, Vector3 } from "three";
import { useNewOrthographicCamera } from "../alignment-modes-commons/align-to-cad-utils";
import {
  SyncCamerasElements,
  useSyncCameras,
} from "../walk-mode/hooks/use-sync-cameras";
import { selectIsPanoAligned } from "../walk-mode/walk-scene-selectors";
import { MoveBothCamerasFromMiniMap, SplitMinMap } from "./split-minimap";
import { SplitInitialState, SplitState } from "./split-state";

/**
 * @returns a two view scene to coordinate left and right views for a split scene
 */
export function SplitScene({
  mainScene,
  mainWalkElement,
  compareScene,
  compareWalkElement,
  initialState,
  mainSceneFilter,
  compareSceneFilter,
}: SplitState & { initialState: SplitInitialState | undefined }): JSX.Element {
  const background = useThree((s) => s.scene.background);

  // Computing all cameras needed in splitscreen mode
  const {
    defaultPerspectiveCamera,
    defaultOrthoCamera,
    secondPerspectiveCamera,
    secondOrthoCamera,
    leftCamera,
    rightCamera,
  } = useDefineSplitSceneCameras(mainWalkElement, compareWalkElement);

  const [activeCamera, setActiveCamera] = useState(leftCamera);
  const shouldSyncCamerasRotation = useAppSelector(
    selectShouldSyncCamerasRotation,
  );
  const shouldSyncCamerasOnWaypoint = useAppSelector(
    selectShouldSyncCamerasOnWaypoint,
  );
  const dispatch = useAppDispatch();

  const [miniMapCameraPosition, setMiniMapCameraPosition] =
    useState<Vector3Prop>();

  const syncCameras = useSyncCameras(
    leftCamera,
    rightCamera,
    mainScene,
    compareScene,
    mainSceneFilter,
    compareSceneFilter,
    mainWalkElement,
    compareWalkElement,
    shouldSyncCamerasOnWaypoint,
    shouldSyncCamerasRotation,
  );

  useLockSettingsFromInitialState(initialState);

  // When the split scene is initialized, this hook sets the position of the right camera.
  // If the compare element is a pano image, the right camera is placed at its origin.
  // If the compare element is a point cloud and the main element a pano image, the right
  // camera is placed at the main pano's origin.
  useSetRightCameraInitialPosition(
    initialState,
    mainWalkElement,
    compareWalkElement,
    rightCamera,
    mainScene.activeSheetFor2DNavigation,
    compareScene.activeSheetFor2DNavigation,
  );

  // When the splitscreen mode is entered with a CAD model, the right screen displays a 360, and the left camera position
  // should be placed at the 360's origin.
  useSetLeftCameraInitialPosition(
    initialState,
    mainWalkElement,
    compareWalkElement,
    leftCamera,
    compareScene.activeSheetFor2DNavigation,
  );

  // Updating both left and right cameras according to a possible given deep link
  useCameraParametersIfAvailable(leftCamera, initialState?.camera);
  useCameraParametersIfAvailable(rightCamera, initialState?.rightCam);

  const cameras = useMemo(
    () => [leftCamera, rightCamera],
    [leftCamera, rightCamera],
  );

  // Storing the active cameras in a context used to compute deep links on demand
  useStoreActiveCameras(cameras);

  const { firstScreen, secondScreen, miniMap, minimapCanvas } =
    useOverlayElements();
  const miniMapActions = useRef<MiniMapActions>(null);

  const shouldShowMainPlaceholders = useAppSelector(
    selectShouldShowPlaceholders(mainWalkElement),
  );
  const shouldShowComparePlaceholders = useAppSelector(
    selectShouldShowPlaceholders(compareWalkElement),
  );

  // The left camera is the default r3f camera used also for other modes.
  // On unmount, the 'View' component writes an invalid aspect ratio prop
  // to the camera. Therefore, on unmount we need to reproportion the left
  // camera according to the r3f canvas' size.
  useReproportionCameraOnUnmount(leftCamera);

  // This callback is issued when the user clicks on a new position in the minimap,
  // and has the effect of animating both cameras to the new position.
  const moveBothCamerasFromMiniMap = useMoveBothCamerasFromMiniMap(
    leftCamera,
    rightCamera,
    mainSceneFilter,
    compareSceneFilter,
    syncCameras,
    mainScene.activeSheetFor2DNavigation,
  );

  // If the main or the compare element are a 2D photo, the corresponding cameras' position are synced
  // with the element's position.
  useSyncCamerasPositionWith2DPhoto(
    mainWalkElement,
    compareWalkElement,
    defaultPerspectiveCamera,
    defaultOrthoCamera,
    secondPerspectiveCamera,
    secondOrthoCamera,
  );

  const photoObject = useActivePhotoObject();

  useSyncActiveCameraWithPhotoObject(
    activeCamera,
    setActiveCamera,
    defaultPerspectiveCamera,
    secondPerspectiveCamera,
  );

  // Couple of callbacks used by both views
  const onCameraMovedViaAnimation = useCallback(
    (p: Vector3Prop) => {
      syncCameras.onCameraMoved();
      setMiniMapCameraPosition(p);
    },
    [syncCameras],
  );

  const onCameraMovedViaControls = useCallback(
    (pos: Vector3) => {
      miniMapActions.current?.centerCameraOn(pos);
    },
    [miniMapActions],
  );

  return (
    <>
      <View
        camera={leftCamera}
        trackingElement={firstScreen}
        background={background}
        hasSeparateScene
      >
        {/** Since it has a separate scene, the snapshot filter shows everything */}
        <Suspense fallback={<SnapshotRenderer filter={showEverything} />}>
          <CameraSync
            source={activeCamera}
            shouldSync={shouldSyncCamerasRotation}
            shouldSyncFov
          />
          <WalkScene
            sheetForElevation={mainScene.activeSheetFor2DNavigation}
            activeElement={mainWalkElement}
            photoObject={
              activeCamera.id === leftCamera.id ? photoObject : undefined
            }
            targetQuaternion={syncCameras.leftCameraQuaternion}
            placeholders={mainScene.panos}
            shouldShowPlaceholders={shouldShowMainPlaceholders}
            paths={mainScene.paths}
            annotations={mainScene.annotations}
            onUserInteracted={() => {
              setActiveCamera(leftCamera);
            }}
            onUserWalkedTo={(p) => {
              syncCameras.setLeftCameraPosition(p);
              setMiniMapCameraPosition(p);
            }}
            onActiveElementChanged={(element) => {
              if (!isIElementModel3dStream(element)) {
                // CAD managed by its own slice using setActiveCad
                dispatch(setActiveElement(element.id));
              }
            }}
            onCameraMovedViaAnimation={onCameraMovedViaAnimation}
            onCameraMovedViaControls={onCameraMovedViaControls}
            onPanoTransformChanged={({ position }) => {
              leftCamera.position.fromArray(position);
              syncCameras.setLeftCameraPosition(position);
              setMiniMapCameraPosition(position);
            }}
            onWayPointChanged={(t) => {
              syncCameras.moveSyncCamerasToNewTarget(t, true);
            }}
            onCameraStartedTranslating={syncCameras.onCameraMoved}
            viewType={ViewType.LeftView}
            onAnimationFinished={() => {
              if (mainSceneFilter === SceneFilter.Pano) {
                syncCameras.setWaitForAutoLockLeft(false);
              }
            }}
          />
        </Suspense>
        {syncCameras.leftCameraPosition &&
          mainSceneFilter !== SceneFilter.Pano && (
            <CameraAnimation
              position={syncCameras.leftCameraPosition}
              quaternion={syncCameras.leftCameraQuaternion}
              duration={getCameraAnimationTime(
                leftCamera,
                syncCameras.leftCameraPosition,
              )}
              onAnimationFinished={() => {
                syncCameras.setLeftCameraPosition(undefined);
                syncCameras.setWaitForAutoLockLeft(false);
              }}
            />
          )}
      </View>

      <View
        camera={rightCamera}
        trackingElement={secondScreen}
        background={background}
        hasSeparateScene
      >
        {/** Since it has a separate scene, the snapshot filter shows everything */}
        <Suspense fallback={<SnapshotRenderer filter={showEverything} />}>
          <CameraSync
            source={activeCamera}
            shouldSync={shouldSyncCamerasRotation}
            shouldSyncFov
          />
          <WalkScene
            sheetForElevation={compareScene.activeSheetFor2DNavigation}
            activeElement={compareWalkElement}
            photoObject={
              activeCamera.id === rightCamera.id ? photoObject : undefined
            }
            targetQuaternion={syncCameras.rightCameraQuaternion}
            placeholders={compareScene.panos}
            shouldShowPlaceholders={shouldShowComparePlaceholders}
            paths={compareScene.paths}
            annotations={compareScene.annotations}
            // ignore active element change in secondary view on purpose, so when the splitscreen closes the main view item is active
            onUserWalkedTo={(p) => {
              syncCameras.setRightCameraPosition(p);
              setMiniMapCameraPosition(p);
            }}
            onUserInteracted={() => {
              setActiveCamera(rightCamera);
            }}
            onCameraMovedViaAnimation={onCameraMovedViaAnimation}
            onCameraMovedViaControls={onCameraMovedViaControls}
            onPanoTransformChanged={({ position }) => {
              rightCamera.position.fromArray(position);
              syncCameras.setRightCameraPosition(position);
              setMiniMapCameraPosition(position);
            }}
            onCameraStartedTranslating={syncCameras.onCameraMoved}
            viewType={ViewType.RightView}
            onActiveElementChanged={(element) => {
              if (isIElementImg360(element)) {
                dispatch(setCompareElementId(element.id));
              }
            }}
            onWayPointChanged={(t) => {
              syncCameras.moveSyncCamerasToNewTarget(t, false);
            }}
            onAnimationFinished={() => {
              if (compareSceneFilter === SceneFilter.Pano) {
                syncCameras.setWaitForAutoLockRight(false);
              }
            }}
          />
        </Suspense>
        {syncCameras.rightCameraPosition &&
          compareSceneFilter !== SceneFilter.Pano && (
            <CameraAnimation
              position={syncCameras.rightCameraPosition}
              quaternion={syncCameras.rightCameraQuaternion}
              duration={getCameraAnimationTime(
                rightCamera,
                syncCameras.rightCameraPosition,
              )}
              onAnimationFinished={() => {
                syncCameras.setRightCameraPosition(undefined);
                syncCameras.setWaitForAutoLockRight(false);
              }}
            />
          )}
      </View>

      {miniMap && secondScreen && (
        <SplitMinMap
          trackingElement={miniMap}
          canvasElement={minimapCanvas}
          actions={miniMapActions}
          mainWalkElement={mainWalkElement}
          compareWalkElement={compareWalkElement}
          mainSceneFilter={mainSceneFilter}
          compareSceneFilter={compareSceneFilter}
          leftCamera={leftCamera}
          rightCamera={rightCamera}
          syncCameras={syncCameras}
          miniMapCameraPosition={miniMapCameraPosition}
          onPlaceholderClicked={moveBothCamerasFromMiniMap}
        />
      )}
    </>
  );
}

function useReproportionCameraOnUnmount(camera: Camera): void {
  const size = useThree((s) => s.size);

  useNonExhaustiveEffect(
    () => () => {
      reproportionCamera(camera, size.width / size.height);
    },
    [camera],
  );
}

function useSyncActiveCameraWithPhotoObject(
  activeCamera: SupportedCamera,
  setActiveCamera: (camera: SupportedCamera) => void,
  defaultPerspectiveCamera: PerspectiveCamera,
  secondPerspectiveCamera: PerspectiveCamera,
): void {
  // Set the correct active camera to make sure cameras can sync properly if needed
  // The event is fired when 2D photo object is clicked in the gallery at left or right screen panel
  const { activatePhotoObjectSplitSide } = useSceneEvents();
  useTypedEvent(activatePhotoObjectSplitSide, (side: SplitSide) => {
    if (
      side === SplitSide.left &&
      activeCamera.id !== defaultPerspectiveCamera.id
    ) {
      setActiveCamera(defaultPerspectiveCamera);
    }
    if (
      side === SplitSide.right &&
      activeCamera.id !== secondPerspectiveCamera.id
    ) {
      setActiveCamera(secondPerspectiveCamera);
    }
  });
}

function useSyncCamerasPositionWith2DPhoto(
  mainWalkElement: WalkSceneActiveElement,
  compareWalkElement: WalkSceneActiveElement,
  defaultPerspectiveCamera: PerspectiveCamera,
  defaultOrthoCamera: OrthographicCamera,
  secondPerspectiveCamera: PerspectiveCamera,
  secondOrthoCamera: OrthographicCamera,
): void {
  const mainElementPosition = useAppSelector(
    selectIElementWorldPosition(mainWalkElement.id),
  );
  const compareElementPosition = useAppSelector(
    selectIElementWorldPosition(compareWalkElement.id),
  );

  // make sure all cameras are synced with 2D photo location in minimap
  useLayoutEffect(() => {
    if (isIElementImg2d(mainWalkElement)) {
      defaultPerspectiveCamera.position.fromArray(mainElementPosition);
      defaultOrthoCamera.position.fromArray(mainElementPosition);
    }
  }, [
    defaultOrthoCamera,
    defaultPerspectiveCamera,
    mainElementPosition,
    mainWalkElement,
  ]);
  useLayoutEffect(() => {
    if (isIElementImg2d(compareWalkElement)) {
      secondPerspectiveCamera.position.fromArray(compareElementPosition);
      secondOrthoCamera.position.fromArray(compareElementPosition);
    }
  }, [
    compareElementPosition,
    compareWalkElement,
    secondOrthoCamera,
    secondPerspectiveCamera,
  ]);
}

function useMoveBothCamerasFromMiniMap(
  leftCamera: SupportedCamera,
  rightCamera: SupportedCamera,
  mainSceneFilter: SceneFilter,
  compareSceneFilter: SceneFilter,
  syncCameras: SyncCamerasElements,
  activeSheetFor2DNavigation: IElementGenericImgSheet | undefined,
): MoveBothCamerasFromMiniMap {
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const shouldSyncCamerasRotation = useAppSelector(
    selectShouldSyncCamerasRotation,
  );
  const shouldSyncCamerasOnWaypoint = useAppSelector(
    selectShouldSyncCamerasOnWaypoint,
  );
  /** This function animates the camera to a new position. The camera locator on the minimap is animated as well. */
  return useCallback(
    (el: IElementImg360) => {
      // when user clicked on placeholder in minimap we always force "Follow" mode
      // to avoid misinterpretation of UI controls by user and over-complication of the logic
      // to reflect all combination of lock/follow/orientationAccurate
      dispatch(setSyncCamerasOnWaypoint(true));

      const camerasAngleInDegrees =
        leftCamera.quaternion.angleTo(rightCamera.quaternion) * RAD_TO_DEG;

      // do not unlock cameras rotation if they are already co-directional (within 1 deg of angular difference)
      if (
        camerasAngleInDegrees > 1 &&
        (mainSceneFilter === SceneFilter.Pano ||
          compareSceneFilter === SceneFilter.Pano)
      ) {
        // if cameras orientation is not the same we unlock rotation then if new target position is accurate,
        //  they will be locked and synced in position and orientation automatically
        dispatch(setSyncCamerasRotation(false));
      }

      syncCameras.syncSplitSceneCameras({
        target: selectBestWayPointForElement(
          el,
          leftCamera,
          activeSheetFor2DNavigation,
          shouldSyncCamerasOnWaypoint,
        )(store.getState()),
        isMainScreen: true,
        dispatch,
        shouldSyncCamerasOnWaypoint: true,
        shouldSyncCamerasRotation,
      });
    },
    [
      dispatch,
      leftCamera,
      rightCamera.quaternion,
      mainSceneFilter,
      compareSceneFilter,
      syncCameras,
      activeSheetFor2DNavigation,
      shouldSyncCamerasOnWaypoint,
      store,
      shouldSyncCamerasRotation,
    ],
  );
}

type SplitSceneCameras = {
  defaultPerspectiveCamera: PerspectiveCamera;
  defaultOrthoCamera: OrthographicCamera;
  secondPerspectiveCamera: PerspectiveCamera;
  secondOrthoCamera: OrthographicCamera;
  leftCamera: SupportedCamera;
  rightCamera: SupportedCamera;
};

function useDefineSplitSceneCameras(
  mainWalkElement: WalkSceneActiveElement,
  compareWalkElement: WalkSceneActiveElement,
): SplitSceneCameras {
  const { defaultOrthoCamera, defaultPerspectiveCamera } = useActiveCamera();
  const secondPerspectiveCamera = useMemo(
    () => defaultPerspectiveCamera.clone(),
    [defaultPerspectiveCamera],
  );
  const secondOrthoCamera = useNewOrthographicCamera();

  const leftCamera = useMemo(
    () =>
      isIElementImg2d(mainWalkElement)
        ? defaultOrthoCamera
        : defaultPerspectiveCamera,
    [defaultOrthoCamera, defaultPerspectiveCamera, mainWalkElement],
  );
  const rightCamera = useMemo(
    () =>
      isIElementImg2d(compareWalkElement)
        ? secondOrthoCamera
        : secondPerspectiveCamera,
    [compareWalkElement, secondOrthoCamera, secondPerspectiveCamera],
  );

  return {
    defaultPerspectiveCamera,
    defaultOrthoCamera,
    secondPerspectiveCamera,
    secondOrthoCamera,
    leftCamera,
    rightCamera,
  };
}

function useSetRightCameraInitialPosition(
  initialState: SplitInitialState | undefined,
  mainWalkElement: WalkSceneActiveElement,
  compareWalkElement: WalkSceneActiveElement,
  rightCamera: SupportedCamera,
  mainActiveSheet: IElementGenericImgSheet | undefined,
  compareActiveSheet: IElementGenericImgSheet | undefined,
): void {
  const store = useAppStore();
  /**
   * Correctly set the initial position of the right/compare camera from
   * when the component is mounted the first time during switch from single screen walk mode
   * to split screen.
   */
  useNonExhaustiveEffect(() => {
    if (initialState && initialState.rightCam) {
      return;
    }
    if (isIElementImg360(compareWalkElement)) {
      const transform = selectPanoCameraTransform(
        compareWalkElement,
        compareActiveSheet,
      )(store.getState());
      rightCamera.position.fromArray(transform.position);
    }
    if (
      isIElementImg360(mainWalkElement) &&
      isIElementGenericPointCloudStream(compareWalkElement)
    ) {
      rightCamera.position.copy(
        selectBestModelCameraFor360(
          mainWalkElement,
          mainActiveSheet,
        )(store.getState()),
      );
    }
  }, []);
}

function useSetLeftCameraInitialPosition(
  initialState: SplitInitialState | undefined,
  mainWalkElement: WalkSceneActiveElement,
  compareWalkElement: WalkSceneActiveElement,
  leftCamera: SupportedCamera,
  compareActiveSheet: IElementGenericImgSheet | undefined,
): void {
  const store = useAppStore();

  // When the splitscreen mode is entered with a CAD model, the right screen displays a 360, and the left camera position
  // should be placed at the 360's origin. In this way, the user has a perfect model-to-reality-capture comparison.
  useNonExhaustiveEffect(() => {
    if (initialState && initialState.camera) {
      return;
    }
    if (
      isIElementModel3dStream(mainWalkElement) &&
      isIElementImg360(compareWalkElement)
    ) {
      const transform = selectPanoCameraTransform(
        compareWalkElement,
        compareActiveSheet,
      )(store.getState());
      leftCamera.position.fromArray(transform.position);
    }
  }, []);
}

const selectShouldShowPlaceholders = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectFeaturesSlice,
      (state: RootState, walkElement: WalkSceneActiveElement) => walkElement,
    ],
    (
      iElements: IElementsState,
      features: FeaturesState,
      walkElement: WalkSceneActiveElement,
    ) =>
      !isIElementImg360(walkElement) ||
      (selectHasValidPose(walkElement)({ iElements }) &&
        selectIsPanoAligned(walkElement)({ iElements, features })),
  ),
);

/**
 *@returns the active 2D photo object
 */
function useActivePhotoObject(): IElementImageCommand | undefined {
  const photoObject = useAppSelector(selectPhotoObject);
  assert(
    !photoObject || isIElementImageCommand(photoObject),
    "Photo object must be image command element",
  );
  return photoObject;
}

function useLockSettingsFromInitialState(
  initialState: SplitInitialState | undefined,
): void {
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (initialState?.syncRot !== undefined) {
      dispatch(setSyncCamerasRotation(initialState.syncRot === "1"));
    }
    if (initialState?.syncWaypoint !== undefined) {
      dispatch(setSyncCamerasOnWaypoint(initialState.syncWaypoint === "1"));
    }
  }, [dispatch, initialState]);
}
